import { useEffect } from 'react';
import { prerenderState } from '../seo/prerenderState';

// Per-route SEO meta for the public pages of an SPA: every route serves the
// same index.html, so without this the 7 whitepapers (and pricing/contact)
// all present Google the homepage title and description. Sets document.title,
// meta description, canonical, and the og/twitter mirrors on mount; the next
// page's hook overwrites them (public pages all use this hook, so nothing
// goes stale between them).

const BASE = 'https://stratiaadmissions.com';

function upsertMeta(attr, key, content) {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
}

// `article` ({ publishedAt, image?, section? }) marks the page as a dated
// piece by the founder (a paper, a news story): the prerender turns it into
// Article/NewsArticle structured data. `newsArticle: true` picks NewsArticle.
// `image` ({ url, width, height, alt }) is the page's own share card.
// `preloadImage` ({ src, srcSet, sizes }) is the hero image the prerendered
// head preloads, so it downloads alongside the app bundle (LCP).
// `jsonLd` is any further schema.org object, or list of them, the page states
// (a college's programs and its FAQPage); the prerender adds each to the head.
export default function usePageMeta({ title, description, path, article, newsArticle = false, jsonLd = null, image = null, preloadImage = null }) {
    // At build time the page's meta goes into its static HTML head (#2567);
    // effects never run there, so record it during render.
    const pre = prerenderState();
    if (pre) pre.meta = { title, description, path, article: article || null, newsArticle, jsonLd, image, preloadImage };
    useEffect(() => {
        if (title) {
            document.title = title;
            upsertMeta('property', 'og:title', title);
            upsertMeta('property', 'twitter:title', title);
        }
        if (description) {
            upsertMeta('name', 'description', description);
            upsertMeta('property', 'og:description', description);
            upsertMeta('property', 'twitter:description', description);
        }
        if (path !== undefined) {
            const url = `${BASE}${path}`;
            let link = document.head.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', url);
            upsertMeta('property', 'og:url', url);
            upsertMeta('property', 'twitter:url', url);
        }
        if (image) {
            // A page with its own share card (a college page's photo card).
            upsertMeta('property', 'og:image', `${BASE}${image.url}`);
            upsertMeta('property', 'twitter:image', `${BASE}${image.url}`);
            upsertMeta('property', 'og:image:alt', image.alt);
        }
    }, [title, description, path, image]);
}
